// =============================================================================
// src/features/auth/auth-api-client.ts — Cliente HTTP da API de auth local.
//
// Fala com server/auth-api-handler.ts (login + redefinição de senha). Fetch é
//   INJETADO (DI) para testes determinísticos; timeout agressivo via
//   AbortController (CLAUDE.md, programação defensiva) — servidor fora do ar
//   vira 'indisponivel' em 8s, nunca um spinner eterno na tela de login.
//
// Contrato de resultado em 3 estados: 'ok' (entrou/enviou), 'recusado' (o
//   servidor respondeu um 4xx/502 com mensagem para o usuário) e 'indisponivel'
//   (rede caiu / timeout / 5xx sem mensagem utilizável).
// =============================================================================

/** Resultado de qualquer chamada de auth (a tela decide a nota por kind). */
export type AuthApiCallResult =
  | { readonly kind: 'ok'; readonly nome: string | null }
  | { readonly kind: 'recusado'; readonly message: string }
  | { readonly kind: 'indisponivel' };

/** Contrato consumido pela tela de login (fake nos testes: FakeAuthApiClient). */
export interface AuthApiClient {
  verifyLogin(email: string, senha: string): Promise<AuthApiCallResult>;
  requestResetCode(email: string): Promise<AuthApiCallResult>;
  confirmReset(email: string, codigo: string, novaSenha: string): Promise<AuthApiCallResult>;
}

/** Dependências injetadas do cliente (fetch default = window.fetch). */
export interface AuthApiClientDeps {
  readonly baseUrl: string;
  readonly fetchImpl?: typeof fetch;
  readonly timeoutMs?: number;
}

/** Timeout default por chamada (defensivo; login não pode pendurar a tela). */
export const AUTH_API_TIMEOUT_MS = 8000;

/** Corpo JSON esperado das respostas da API ({ok}/{error,message}). */
interface AuthApiResponseBody {
  readonly nome?: unknown;
  readonly message?: unknown;
}

/** Parse best-effort do corpo (resposta sem JSON vira objeto vazio). */
async function parseAuthResponseBody(response: Response): Promise<AuthApiResponseBody> {
  try {
    return (await response.json()) as AuthApiResponseBody;
  } catch {
    return {};
  }
}

/** Mapeia a Response HTTP para o contrato de 3 estados. */
async function toAuthApiCallResult(response: Response): Promise<AuthApiCallResult> {
  const body = await parseAuthResponseBody(response);
  if (response.ok) {
    return { kind: 'ok', nome: typeof body.nome === 'string' ? body.nome : null };
  }
  if (typeof body.message === 'string' && body.message.length > 0) {
    return { kind: 'recusado', message: body.message };
  }
  return { kind: 'indisponivel' };
}

/**
 * Cria o cliente da API de auth.
 *
 * @example
 *   const client = createAuthApiClient({ baseUrl: resolveAuthApiBaseUrl(import.meta.env) });
 *   const result = await client.verifyLogin('nome@r2tgroup.com', 's3nh4');
 */
export function createAuthApiClient(deps: AuthApiClientDeps): AuthApiClient {
  const fetchImpl = deps.fetchImpl ?? fetch;
  const timeoutMs = deps.timeoutMs ?? AUTH_API_TIMEOUT_MS;

  // Sem try/finally: o bloco finally gera branch implícito que o v8 não consegue
  // atribuir em função async (quebraria o gate de 100% sem ganho real).
  async function postAuthJson(path: string, payload: Record<string, string>): Promise<AuthApiCallResult> {
    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(), timeoutMs);
    let result: AuthApiCallResult;
    try {
      const response = await fetchImpl(`${deps.baseUrl}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: abort.signal,
      });
      result = await toAuthApiCallResult(response);
    } catch {
      result = { kind: 'indisponivel' };
    }
    clearTimeout(timer);
    return result;
  }

  return {
    verifyLogin: (email, senha) => postAuthJson('/api/auth/login', { email, senha }),
    requestResetCode: (email) => postAuthJson('/api/auth/reset-request', { email }),
    confirmReset: (email, codigo, novaSenha) =>
      postAuthJson('/api/auth/reset-confirm', { email, codigo, novaSenha }),
  };
}
